<template>
  <IntercomLayout>
    <template #topbar>
      <IntercomTopbar
        app-name="OnePlaceHQ"
        product-name="Billing"
        :business-name="businessName"
        :user="user"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="bill-detail-view">
      <!-- Back link -->
      <button class="back-link" @click="router.push('/purchase-bills')">
        &larr; Back to Purchase Bills
      </button>

      <div v-if="isLoading" class="loading-state">Loading...</div>

      <div v-else-if="bill" class="bill-content">
        <!-- Header -->
        <div class="page-header">
          <div>
            <h1 class="page-title">{{ bill.bill_number }}</h1>
            <p class="page-subtitle">{{ bill.vendor_name }}</p>
          </div>
          <div class="header-actions">
            <IntercomBadge :variant="getStatusVariant(bill.status)">
              {{ capitalize(bill.status) }}
            </IntercomBadge>
            <IntercomBadge :variant="getPaymentVariant(bill.payment_status)">
              {{ capitalize(bill.payment_status) }}
            </IntercomBadge>
          </div>
        </div>

        <!-- Bill Info -->
        <IntercomCard>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Bill Date</span>
              <span class="info-value">{{ formatDate(bill.bill_date) }}</span>
            </div>
            <div v-if="bill.due_date" class="info-item">
              <span class="info-label">Due Date</span>
              <span class="info-value">{{ formatDate(bill.due_date) }}</span>
            </div>
            <div v-if="bill.vendor_bill_number" class="info-item">
              <span class="info-label">Vendor Bill #</span>
              <span class="info-value">{{ bill.vendor_bill_number }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Total Amount</span>
              <span class="info-value highlight">{{ formatCurrency(bill.total_amount) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Amount Paid</span>
              <span class="info-value">{{ formatCurrency(bill.amount_paid) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Amount Due</span>
              <span class="info-value" :class="{ 'text-danger': bill.amount_due > 0 }">
                {{ formatCurrency(bill.amount_due) }}
              </span>
            </div>
          </div>
          <div v-if="bill.notes" class="bill-notes">
            <span class="info-label">Notes:</span> {{ bill.notes }}
          </div>
        </IntercomCard>

        <!-- Line Items -->
        <IntercomCard>
          <h3 class="section-heading">Line Items</h3>
          <IntercomTable :columns="itemColumns" :data="bill.items || []">
            <template #cell-quantity="{ value }">
              {{ parseFloat(Number(value).toFixed(3)) }}
            </template>
            <template #cell-unit_price="{ value }">
              {{ formatCurrency(value) }}
            </template>
            <template #cell-taxable_amount="{ value }">
              {{ formatCurrency(value) }}
            </template>
            <template #cell-tax_amount="{ value }">
              {{ formatCurrency(value) }}
            </template>
            <template #cell-line_total="{ value }">
              <strong>{{ formatCurrency(value) }}</strong>
            </template>
          </IntercomTable>
        </IntercomCard>

        <!-- Tax Summary -->
        <IntercomCard v-if="bill.total_tax_amount > 0">
          <h3 class="section-heading">Tax Summary</h3>
          <div class="tax-grid">
            <div class="tax-item">
              <span class="info-label">Subtotal (Taxable)</span>
              <span class="info-value">{{ formatCurrency(bill.subtotal) }}</span>
            </div>
            <div v-if="bill.cgst_amount > 0" class="tax-item">
              <span class="info-label">CGST</span>
              <span class="info-value cgst">{{ formatCurrency(bill.cgst_amount) }}</span>
            </div>
            <div v-if="bill.sgst_amount > 0" class="tax-item">
              <span class="info-label">SGST</span>
              <span class="info-value sgst">{{ formatCurrency(bill.sgst_amount) }}</span>
            </div>
            <div v-if="bill.igst_amount > 0" class="tax-item">
              <span class="info-label">IGST</span>
              <span class="info-value igst">{{ formatCurrency(bill.igst_amount) }}</span>
            </div>
            <div class="tax-item total">
              <span class="info-label">Grand Total</span>
              <span class="info-value">{{ formatCurrency(bill.total_amount) }}</span>
            </div>
          </div>
        </IntercomCard>

        <!-- Actions -->
        <div class="action-bar">
          <IntercomButton
            v-if="bill.status === 'draft' || bill.status === 'finalized'"
            variant="secondary"
            @click="showEditModal = true"
          >
            Edit Bill
          </IntercomButton>
          <IntercomButton
            v-if="bill.status === 'draft'"
            variant="primary"
            @click="handleFinalize"
          >
            Finalize Bill
          </IntercomButton>
          <IntercomButton
            v-if="bill.status === 'finalized' && bill.payment_status !== 'paid'"
            variant="primary"
            @click="showPaymentModal = true"
          >
            Record Payment
          </IntercomButton>
        </div>
      </div>

      <div v-else class="empty-state">Purchase bill not found</div>

      <!-- Edit Purchase Bill Modal -->
      <PurchaseBillForm
        v-model:show="showEditModal"
        :bill="bill"
        @success="handleEditSuccess"
      />

      <!-- Payment Modal -->
      <PurchasePaymentForm
        v-model:show="showPaymentModal"
        :bill="bill"
        @success="handlePaymentSuccess"
      />
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuth } from '@shared/composables/useAuth.js'
import { useConfirm } from '@shared/composables/useConfirm.js'
import { useBilling } from '../composables/useBilling.js'
import {
  IntercomLayout,
  IntercomTopbar,
  IntercomSidebar,
  IntercomCard,
  IntercomTable,
  IntercomButton,
  IntercomBadge,
} from '@shared/components/intercom'
import PurchaseBillForm from '../components/PurchaseBillForm.vue'
import PurchasePaymentForm from '../components/PurchasePaymentForm.vue'
import { billingNavSections } from '../config/navigation.js'
import { formatDate } from '../utils/billingUtils.js'

const route = useRoute()
const router = useRouter()
const { user, logout } = useAuth()
const { confirm } = useConfirm()
const { getPurchaseBill, finalizePurchaseBill, businessName, fetchBusinessName } = useBilling()

const navSections = billingNavSections

const bill = ref(null)
const isLoading = ref(true)
const showPaymentModal = ref(false)
const showEditModal = ref(false)

const itemColumns = [
  { key: 'product_name', title: 'Product', width: '200px' },
  { key: 'hsn_code', title: 'HSN', width: '100px' },
  { key: 'quantity', title: 'Qty', align: 'right', width: '80px' },
  { key: 'unit_price', title: 'Unit Price', align: 'right', width: '120px' },
  { key: 'taxable_amount', title: 'Taxable', align: 'right', width: '120px' },
  { key: 'tax_amount', title: 'Tax', align: 'right', width: '100px' },
  { key: 'line_total', title: 'Total', align: 'right', width: '120px' },
]

const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : ''

const formatCurrency = (val) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(val || 0)

const getStatusVariant = (s) => {
  if (s === 'finalized') return 'success'
  if (s === 'cancelled') return 'error'
  return 'default'
}

const getPaymentVariant = (s) => {
  if (s === 'paid') return 'success'
  if (s === 'partial') return 'warning'
  return 'default'
}

const fetchBill = async () => {
  isLoading.value = true
  try {
    const response = await getPurchaseBill(route.params.id)
    bill.value = response.purchase_bill || response
  } catch (error) {
    console.error('Failed to fetch purchase bill:', error)
    bill.value = null
  } finally {
    isLoading.value = false
  }
}

const handleFinalize = async () => {
  const ok = await confirm(`Finalize bill "${bill.value.bill_number}"? This cannot be undone.`, { title: 'Finalize Purchase Bill' })
  if (!ok) return
  try {
    await finalizePurchaseBill(bill.value.id)
    await fetchBill()
  } catch (error) {
    console.error('Failed to finalize:', error)
  }
}

const handleEditSuccess = async () => {
  showEditModal.value = false
  await fetchBill()
}

const handlePaymentSuccess = async () => {
  showPaymentModal.value = false
  await fetchBill()
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    router.push('/login')
  }
}

onMounted(() => {
  fetchBusinessName()
  fetchBill()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.bill-detail-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-2) 0;
  margin-bottom: var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-blue);
  background: none;
  border: none;
  cursor: pointer;
}

.back-link:hover {
  text-decoration: underline;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: var(--space-6);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin: var(--space-1) 0 0;
}

.header-actions {
  display: flex;
  gap: var(--space-2);
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: var(--space-4);
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.info-label {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-500);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
}

.info-value.highlight {
  color: var(--color-blue);
  font-size: var(--font-size-lg);
}

.info-value.cgst { color: #2563eb; }
.info-value.sgst { color: #7c3aed; }
.info-value.igst { color: #0891b2; }

.text-danger {
  color: #dc2626;
}

.bill-notes {
  margin-top: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-gray-200);
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
}

.section-heading {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.tax-grid {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  max-width: 350px;
}

.tax-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tax-item.total {
  padding-top: var(--space-3);
  border-top: 2px solid var(--color-gray-300);
}

.tax-item.total .info-value {
  font-size: var(--font-size-lg);
  color: var(--color-blue);
  font-weight: var(--font-weight-bold);
}

.action-bar {
  display: flex;
  gap: var(--space-3);
  margin-top: var(--space-6);
}

.loading-state,
.empty-state {
  text-align: center;
  padding: var(--space-10);
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
}

.bill-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

@media (max-width: 768px) {
  .bill-detail-view { padding: var(--space-4); }
  .page-header { flex-direction: column; gap: var(--space-3); }
  .info-grid { grid-template-columns: repeat(2, 1fr); }
}
</style>
